import { Button } from '@/components/common/button'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogContentWrapper,
  DialogHeader,
  DialogHeaderWrapper,
  DialogScrollArea,
  DialogTitle,
  DialogTrigger,
} from '@/components/common/dialog'
import { ExpansionArrowsIcon } from '@/components/icons/expansionArrows'
import type { BorrowMarketAssetData, BorrowMarketHistoricalDataPoint } from '@/utils/internalAppSdk'
import type { ReactNode } from 'react'
import { BORROW_MARKET_DETAILS_STAT_VALUES, type BorrowMarketDetailsStat } from './borrowMarketDetailsStats.constants'
import { BorrowMarketDetailsStatsContent } from './borrowMarketDetailsStatsContent'

interface Props {
  borrowAssetData: BorrowMarketAssetData
  chartData: BorrowMarketHistoricalDataPoint[]
  isChartError: boolean
  isChartFetching: boolean
  rangeSelector: ReactNode
}

const CHART_TITLES: Record<BorrowMarketDetailsStat, string> = {
  [BORROW_MARKET_DETAILS_STAT_VALUES.borrowApy]: 'Borrow APY',
  [BORROW_MARKET_DETAILS_STAT_VALUES.utilization]: 'Utilization',
  [BORROW_MARKET_DETAILS_STAT_VALUES.totalSupplied]: 'Total supplied',
}

export const BorrowMarketDetailsStatsCharts = ({
  borrowAssetData,
  chartData,
  isChartError,
  isChartFetching,
  rangeSelector,
}: Props) => {
  const contentProps = { borrowAssetData, chartData, isChartError, isChartFetching }

  return (
    <div className="flex flex-col gap-4">
      {Object.values(BORROW_MARKET_DETAILS_STAT_VALUES).map((stat) => (
        <BorrowMarketDetailsChart
          key={stat}
          title={CHART_TITLES[stat]}
          rangeSelector={rangeSelector}
          fullView={
            <BorrowMarketDetailsStatsContent
              {...contentProps}
              stat={stat}
              rangeSelector={rangeSelector}
              chartClassName="h-80 min-h-80"
              showBrush
            />
          }
        >
          <BorrowMarketDetailsStatsContent {...contentProps} stat={stat} chartClassName="h-80 min-h-80" />
        </BorrowMarketDetailsChart>
      ))}
    </div>
  )
}

interface BorrowMarketDetailsChartProps {
  title: string
  children: ReactNode
  fullView: ReactNode
  rangeSelector: ReactNode
}

const BorrowMarketDetailsChart = ({ title, children, fullView, rangeSelector }: BorrowMarketDetailsChartProps) => (
  <section className="flex flex-col gap-3">
    {children}
    <div className="flex items-center justify-between gap-2">
      {rangeSelector}
      <Dialog>
        <DialogTrigger asChild>
          <Button
            aria-label={`Show ${title} in full view`}
            variant="default"
            size="default"
            className="flex items-center gap-1 rounded-full text-body-foreground text-sm"
          >
            Show in full view
            <ExpansionArrowsIcon expanded={false} size={16} />
          </Button>
        </DialogTrigger>
        <DialogContent hideCloseButton className="max-w-5xl bg-page lg:max-h-[90vh]">
          <DialogBody>
            <DialogHeaderWrapper className="gap-0 pt-6 md:pt-6">
              <DialogHeader className="flex flex-col">
                <DialogTitle className="sr-only">{title} - detailed view</DialogTitle>
                <nav className="mb-4 ml-auto w-fit">
                  <Button size="default" variant="default" className="gap-1 rounded-full text-sm" asChild>
                    <DialogClose>
                      Go to default view
                      <ExpansionArrowsIcon expanded size={16} />
                    </DialogClose>
                  </Button>
                </nav>
              </DialogHeader>
            </DialogHeaderWrapper>
            <DialogScrollArea>
              <DialogContentWrapper className="pt-0 md:pt-0">{fullView}</DialogContentWrapper>
            </DialogScrollArea>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </div>
  </section>
)
